import dayjs from "dayjs";
import { SavedFilter } from "../components/Shared/Filters/types";
import { JOB_STATUS } from "../configs/constants/JobStatus";
import type { GetJobsParams } from "../networking/job.hooks";

/**
 * A relative date token. Resolved to concrete dates at query-build time (see
 * `definitionToJobParams`) so a saved "today" filter always means *today*, not
 * the day it was saved.
 */
export type RelativeDatePreset = "today" | "tomorrow" | "thisWeek" | "next7days";

/**
 * The "when" of the date filter — the date(s) the filter is about. Either a
 * relative token, a single specific day, or an explicit range. `undefined` on
 * the definition means "any date" (no date constraint).
 */
export type JobWhen =
	| { kind: RelativeDatePreset }
	| { kind: "date"; date?: string }
	| { kind: "range"; start?: string; end?: string };

/**
 * The relationship between a job and the selected `when` — i.e. *how* the dates
 * are matched. Reads as a sentence: "show jobs that {relation} {when}".
 * - `ongoing`: the job overlaps the period (even without a shift on those days).
 * - `open`: the job has an open (unmanned) shift on any selected day.
 * - `booked`: the job has a booked shift on any selected day.
 * - `any`: the job has any shift (open or booked) on any selected day.
 * Defaults to `ongoing` when omitted.
 */
export type JobDateRelation = "ongoing" | "open" | "booked" | "any";

/**
 * A structured filter over the jobs list. Unlike the consultant/customer
 * definitions this is NOT evaluated client-side (there is no `matchesJob`) —
 * jobs stay server-paged, so a definition is translated into `GET /job` query
 * params by `definitionToJobParams`. The definition itself only drives the
 * filter UI and the active-pill state.
 *
 * Semantics: AND across groups, OR within a multi-select group. An omitted /
 * empty group is "no constraint".
 */
export interface JobFilterDefinition {
	status?: JOB_STATUS[];
	customerIds?: string[];
	contractIds?: string[];
	pools?: string[];
	/** descriptive job tag keys (org `jobTagTypes`) — matches jobs carrying ALL selected keys */
	tags?: string[];
	/**
	 * customer tag keys (org `customerTagTypes`) — matches jobs whose CUSTOMER
	 * carries ANY selected key. Resolved server-side against the customer
	 * register, since tags live on the customer rather than the job.
	 */
	customerTags?: string[];
	/**
	 * Matching-gate requirements (`job.requirements`, the "Kompetenskrav"):
	 * consultant tag keys (org `consultantTagTypes`) and certification type keys
	 * (org `certificationTypes`) the job must REQUIRE. AND semantics within and
	 * across the two lists — the job must require ALL selected keys.
	 */
	requirementTags?: string[];
	requirementCertifications?: string[];
	minDays?: number;
	minHours?: number;
	/** The date(s) the filter is about. Omitted = no date constraint. */
	when?: JobWhen;
	/** How `when` is matched. Meaningless (and ignored) without a `when`. */
	dateRelation?: JobDateRelation;
}

/** A named, saved job filter (org preset or personal). */
export type JobSavedFilter = SavedFilter<JobFilterDefinition>;

export const EMPTY_JOB_DEFINITION: JobFilterDefinition = {};

/** A definition with no active constraints (the "All" baseline). */
export function isEmptyJobDefinition(def: JobFilterDefinition): boolean {
	return (
		!def.status?.length &&
		!def.customerIds?.length &&
		!def.contractIds?.length &&
		!def.pools?.length &&
		!def.tags?.length &&
		!def.customerTags?.length &&
		!def.requirementTags?.length &&
		!def.requirementCertifications?.length &&
		def.minDays == null &&
		def.minHours == null &&
		!def.when
	);
}

/**
 * Canonical JSON for a definition so two definitions that differ only in array
 * ordering or empty/omitted groups compare equal. Used to tell which pill (if
 * any) the current filter corresponds to.
 */
function canonicalize(def: JobFilterDefinition): string {
	const n: Record<string, unknown> = {};

	if (def.status?.length) n.status = [...def.status].sort();
	if (def.customerIds?.length) n.customerIds = [...def.customerIds].sort();
	if (def.contractIds?.length) n.contractIds = [...def.contractIds].sort();
	if (def.pools?.length) n.pools = [...def.pools].sort();
	if (def.tags?.length) n.tags = [...def.tags].sort();
	if (def.customerTags?.length) n.customerTags = [...def.customerTags].sort();
	if (def.requirementTags?.length) n.requirementTags = [...def.requirementTags].sort();
	if (def.requirementCertifications?.length)
		n.requirementCertifications = [...def.requirementCertifications].sort();
	if (def.minDays != null) n.minDays = def.minDays;
	if (def.minHours != null) n.minHours = def.minHours;
	// `when` is a small object with a fixed key order as built by the UI, so a
	// plain stringify is stable enough for equality. dateRelation only matters
	// when a `when` is set, and "ongoing" is the default so it omits to nothing.
	if (def.when) {
		n.when = def.when;
		if (def.dateRelation && def.dateRelation !== "ongoing") n.dateRelation = def.dateRelation;
	}

	return JSON.stringify(n);
}

export function jobDefinitionsEqual(a: JobFilterDefinition, b: JobFilterDefinition): boolean {
	return canonicalize(a) === canonicalize(b);
}

/**
 * Expand a relative preset to the concrete day(s) it covers, anchored to the
 * user's current clock (same approach as the existing job tabs' `dayjs()` use).
 * `today`/`tomorrow` are a single day; `thisWeek`/`next7days` are seven days.
 */
function relativeDays(preset: RelativeDatePreset): dayjs.Dayjs[] {
	const today = dayjs().startOf("day");
	switch (preset) {
		case "today":
			return [today];
		case "tomorrow":
			return [today.add(1, "day")];
		case "thisWeek": {
			const start = today.startOf("week");
			return Array.from({ length: 7 }, (_, i) => start.add(i, "day"));
		}
		case "next7days":
			return Array.from({ length: 7 }, (_, i) => today.add(i, "day"));
	}
}

/**
 * Enumerate the individual calendar days (YYYY-MM-DD) covered by an absolute
 * range. With a single bound it's that one day; with both it's every day from
 * start to end inclusive (capped at ~1 year as a runaway guard). Used to feed
 * the shifts-at-date params, which take a list of dates rather than a range.
 */
function enumerateDays(start?: string, end?: string): string[] {
	if (!start && !end) return [];
	let cursor = dayjs(start ?? end).startOf("day");
	const last = dayjs(end ?? start).startOf("day");
	const days: string[] = [];
	while ((cursor.isBefore(last) || cursor.isSame(last)) && days.length <= 366) {
		days.push(cursor.format("YYYY-MM-DD"));
		cursor = cursor.add(1, "day");
	}
	return days;
}

/** The concrete YYYY-MM-DD days a `when` covers (for the shifts-at-date params). */
function whenToDayStrings(when: JobWhen): string[] {
	switch (when.kind) {
		case "today":
		case "tomorrow":
		case "thisWeek":
		case "next7days":
			return relativeDays(when.kind).map((d) => d.format("YYYY-MM-DD"));
		case "date":
			return when.date ? [dayjs(when.date).format("YYYY-MM-DD")] : [];
		case "range":
			return enumerateDays(when.start, when.end);
	}
}

/** The ISO start/end span a `when` covers (for the overlapping-range params). */
function whenToSpan(when: JobWhen): { start?: string; end?: string } {
	switch (when.kind) {
		case "today":
		case "tomorrow":
		case "thisWeek":
		case "next7days": {
			const days = relativeDays(when.kind);
			return {
				start: days[0].startOf("day").toISOString(),
				end: days[days.length - 1].endOf("day").toISOString(),
			};
		}
		case "date":
			return when.date
				? {
						start: dayjs(when.date).startOf("day").toISOString(),
						end: dayjs(when.date).endOf("day").toISOString(),
				  }
				: {};
		case "range":
			return { start: when.start, end: when.end };
	}
}

/**
 * Translate a definition into the existing `GET /job` query params, resolving
 * relative date tokens client-side. Multi-select groups are emitted as the
 * CSV-style params the backend understands (`status[]`, and comma-joined
 * `customerId`/`contractId`/`pool` → Mongo `$in`). Returns only the params the
 * definition constrains; the page merges these with paging/sort/keyword.
 */
export function definitionToJobParams(def: JobFilterDefinition): Partial<GetJobsParams> {
	const params: Partial<GetJobsParams> = {};

	if (def.status?.length) params.status = def.status;
	if (def.customerIds?.length) params.customerId = def.customerIds.join(",");
	if (def.contractIds?.length) params.contractId = def.contractIds.join(",");
	if (def.pools?.length) params.pool = def.pools.join(",");
	if (def.tags?.length) params.tags = def.tags.join(",");
	if (def.customerTags?.length) params.customerTags = def.customerTags.join(",");
	if (def.requirementTags?.length) params.requirementTags = def.requirementTags.join(",");
	if (def.requirementCertifications?.length)
		params.requirementCertifications = def.requirementCertifications.join(",");
	if (def.minDays != null) params.minDays = def.minDays;
	if (def.minHours != null) params.minHours = def.minHours;

	if (def.when) {
		const relation: JobDateRelation = def.dateRelation ?? "ongoing";

		if (relation === "ongoing") {
			// "Ongoing" means jobs active during the period, so include jobs that
			// merely overlap the span rather than only those fully contained.
			const span = whenToSpan(def.when);
			if (span.start) params.startDate = span.start;
			if (span.end) params.endDate = span.end;
			if (span.start && span.end) params.includeOverlappingRange = true;
		} else {
			// open / booked / any: the job must have a shift of that kind on one of
			// the selected days, expressed as a list of YYYY-MM-DD dates.
			const days = whenToDayStrings(def.when);
			if (days.length) {
				if (relation === "open") params.openShiftsAt = days;
				else if (relation === "booked") params.bookedShiftsAt = days;
				else params.anyShiftsAt = days;
			}
		}
	}

	return params;
}
